/**
 * PagBrasil Style Mapper
 * 
 * Converts Altruon styleCustomization object to CSS styles for PagBrasil card inputs.
 * Unlike dLocal/Adyen which use iframes, PagBrasil uses regular HTML inputs,
 * so we can apply CSS styles directly.
 * 
 * @see https://pagbrasil.atlassian.net/wiki/spaces/PBDocs/pages/98666009/PagBrasil.JS
 */

/**
 * Maps Altruon style object to CSS styles for card input fields
 * 
 * @param {Object} altruonStyles - The Altruon styleCustomization object from StyleContext
 * @returns {Object} CSS style object for input elements
 */
export const mapStylesToCardInputs = (altruonStyles) => {
  if (!altruonStyles) return getDefaultInputStyles();

  const {
    components = {},
    colors = {},
    typography = {},
    borderRadius = {},
    borderWidth = {},
  } = altruonStyles;

  const input = components.input || {};

  // Ensure we always have a visible text color
  const textColor = input.textColor || colors.textPrimary || '#111827';
  const finalTextColor = textColor && textColor !== 'transparent' && textColor !== 'rgba(0,0,0,0)'
    ? textColor
    : '#111827';

  return {
    color: finalTextColor,
    fontSize: input.fontSize || typography.fontSizes?.md || '16px',
    fontFamily: typography.fontFamily || 'Arial, sans-serif',
    fontWeight: input.fontWeight || typography.fontWeights?.normal || '400',
    lineHeight: typography.lineHeight?.normal || '1.5',
    backgroundColor: input.backgroundColor || colors.surface || '#FFFFFF',
    borderColor: input.borderColor || colors.border || '#D1D5DB',
    borderWidth: input.borderWidth || borderWidth.default || '1px',
    borderStyle: 'solid',
    borderRadius: input.borderRadius || borderRadius.md || '8px',
    padding: input.padding || '12px 16px',
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  };
};

/**
 * Maps Altruon style object to CSS styles for focused input state
 * 
 * @param {Object} altruonStyles - The Altruon styleCustomization object
 * @returns {Object} CSS style object for focused input state
 */
export const mapStylesToFocusedInput = (altruonStyles) => {
  if (!altruonStyles) return getDefaultFocusStyles();

  const { colors = {}, components = {} } = altruonStyles;
  const input = components.input || {};

  return {
    borderColor: input.focusBorderColor || colors.primary || '#3B82F6',
    boxShadow: `0 0 0 3px ${(colors.primary || '#3B82F6')}20`,
  };
};

/**
 * Maps Altruon style object to CSS styles for error input state
 * 
 * @param {Object} altruonStyles - The Altruon styleCustomization object
 * @returns {Object} CSS style object for error input state
 */
export const mapStylesToErrorInput = (altruonStyles) => {
  if (!altruonStyles) return getDefaultErrorStyles();

  const { colors = {}, components = {} } = altruonStyles;

  return {
    borderColor: colors.error || components.errorMessage?.color || '#EF4444',
    boxShadow: `0 0 0 3px ${(colors.error || '#EF4444')}20`,
  };
};

/**
 * Maps Altruon style object to CSS styles for labels
 * 
 * @param {Object} altruonStyles - The Altruon styleCustomization object
 * @returns {Object} CSS style object for label elements
 */
export const mapStylesToLabels = (altruonStyles) => {
  if (!altruonStyles) return getDefaultLabelStyles();

  const { colors = {}, typography = {}, components = {} } = altruonStyles;
  const label = components.label || {};

  return {
    color: label.color || colors.textPrimary || '#111827',
    fontSize: label.fontSize || typography.fontSizes?.sm || '14px',
    fontFamily: typography.fontFamily || 'Arial, sans-serif',
    fontWeight: label.fontWeight || typography.fontWeights?.medium || '500',
    marginBottom: '8px',
    display: 'block',
  };
};

/**
 * Maps Altruon style object to CSS styles for error messages
 * 
 * @param {Object} altruonStyles - The Altruon styleCustomization object
 * @returns {Object} CSS style object for error message elements
 */
export const mapStylesToErrorMessage = (altruonStyles) => {
  if (!altruonStyles) return getDefaultErrorMessageStyles();

  const { colors = {}, typography = {}, components = {} } = altruonStyles;
  const errorMessage = components.errorMessage || {};

  return {
    color: errorMessage.color || colors.error || '#EF4444',
    fontSize: errorMessage.fontSize || typography.fontSizes?.sm || '14px',
    fontFamily: typography.fontFamily || 'Arial, sans-serif',
    marginTop: '4px',
  };
};

/**
 * Default input styles (fallback)
 */
const getDefaultInputStyles = () => ({
  color: '#111827',
  fontSize: '16px',
  fontFamily: 'Arial, sans-serif',
  fontWeight: '400',
  lineHeight: '1.5',
  backgroundColor: '#FFFFFF',
  borderColor: '#D1D5DB',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderRadius: '8px',
  padding: '12px 16px',
  outline: 'none',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
});

/**
 * Default focus styles (fallback)
 */
const getDefaultFocusStyles = () => ({
  borderColor: '#3B82F6',
  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.12)',
});

/**
 * Default error styles (fallback)
 */
const getDefaultErrorStyles = () => ({
  borderColor: '#EF4444',
  boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.12)',
});

/**
 * Default label styles (fallback)
 */
const getDefaultLabelStyles = () => ({
  color: '#111827',
  fontSize: '14px',
  fontFamily: 'Arial, sans-serif',
  fontWeight: '500',
  marginBottom: '8px',
  display: 'block',
});

/**
 * Default error message styles (fallback)
 */
const getDefaultErrorMessageStyles = () => ({
  color: '#EF4444',
  fontSize: '14px',
  fontFamily: 'Arial, sans-serif',
  marginTop: '4px',
});

/**
 * Generate inline style string from style object
 * Useful for applying styles directly to elements
 * 
 * @param {Object} styleObject - CSS style object
 * @returns {string} Inline style string
 */
export const styleObjectToString = (styleObject) => {
  return Object.entries(styleObject)
    .map(([key, value]) => {
      // Convert camelCase to kebab-case
      const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      return `${cssKey}: ${value}`;
    })
    .join('; ');
};

/**
 * Brazilian state codes for validation
 */
export const BRAZILIAN_STATES = [
  { code: 'AC', name: 'Acre' },
  { code: 'AL', name: 'Alagoas' },
  { code: 'AP', name: 'Amapá' },
  { code: 'AM', name: 'Amazonas' },
  { code: 'BA', name: 'Bahia' },
  { code: 'CE', name: 'Ceará' },
  { code: 'DF', name: 'Distrito Federal' },
  { code: 'ES', name: 'Espírito Santo' },
  { code: 'GO', name: 'Goiás' },
  { code: 'MA', name: 'Maranhão' },
  { code: 'MT', name: 'Mato Grosso' },
  { code: 'MS', name: 'Mato Grosso do Sul' },
  { code: 'MG', name: 'Minas Gerais' },
  { code: 'PA', name: 'Pará' },
  { code: 'PB', name: 'Paraíba' },
  { code: 'PR', name: 'Paraná' },
  { code: 'PE', name: 'Pernambuco' },
  { code: 'PI', name: 'Piauí' },
  { code: 'RJ', name: 'Rio de Janeiro' },
  { code: 'RN', name: 'Rio Grande do Norte' },
  { code: 'RS', name: 'Rio Grande do Sul' },
  { code: 'RO', name: 'Rondônia' },
  { code: 'RR', name: 'Roraima' },
  { code: 'SC', name: 'Santa Catarina' },
  { code: 'SP', name: 'São Paulo' },
  { code: 'SE', name: 'Sergipe' },
  { code: 'TO', name: 'Tocantins' },
];


const stripAccents = (value) =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

/** Map full state names or codes to PagBrasil's 2-letter UF code. */
export const normalizeBrazilianStateCode = (state) => {
  if (!state || typeof state !== "string") return "";
  const trimmed = state.trim();
  if (!trimmed) return "";
  if (trimmed.length === 2) return trimmed.toUpperCase();

  const normalized = stripAccents(trimmed);
  const match = BRAZILIAN_STATES.find(
    (entry) =>
      entry.code.toUpperCase() === trimmed.toUpperCase() ||
      stripAccents(entry.name) === normalized
  );  
  return match?.code || trimmed.slice(0, 2).toUpperCase();
};

/**
 * Get card brand from card number prefix
 * 
 * @param {string} cardNumber - Card number (first 6 digits are enough)
 * @returns {string} Card brand name
 */
export const getCardBrandFromNumber = (cardNumber) => {
  if (!cardNumber) return null;
  
  const num = cardNumber.replace(/\s/g, '');
  
  // Visa
  if (/^4/.test(num)) return 'Visa';
  
  // Mastercard
  if (/^5[1-5]/.test(num) || /^2[2-7]/.test(num)) return 'Mastercard';
  
  // American Express
  if (/^3[47]/.test(num)) return 'American Express';
  
  // Elo (Brazilian)
  if (/^(636368|438935|504175|451416|636297|5067|4576|4011)/.test(num)) return 'Elo';
  
  // Hipercard (Brazilian)
  if (/^(606282|3841)/.test(num)) return 'Hipercard';
  
  // Diners Club
  if (/^3(?:0[0-5]|[68])/.test(num)) return 'Diners Club';
  
  // Discover
  if (/^6(?:011|5)/.test(num)) return 'Discover';
  
  // JCB
  if (/^(?:2131|1800|35)/.test(num)) return 'JCB';
  
  return null;
};

/**
 * Format card number with spaces for display
 * 
 * @param {string} cardNumber - Raw card number
 * @returns {string} Formatted card number
 */
export const formatCardNumber = (cardNumber) => {
  if (!cardNumber) return '';
  const num = cardNumber.replace(/\s/g, '').replace(/\D/g, '');
  const groups = num.match(/.{1,4}/g);
  return groups ? groups.join(' ') : num;
};

/**
 * Format expiration date as MM/YY
 * 
 * @param {string} value - Raw input value
 * @returns {string} Formatted expiration date
 */
export const formatExpirationDate = (value) => {
  if (!value) return '';
  const num = value.replace(/\D/g, '');
  if (num.length >= 2) {
    return `${num.slice(0, 2)}/${num.slice(2, 4)}`;
  }
  return num;
};
